import type { TileId } from '../../content/tiles';
import type { Direction } from '../../sim/direction';
import { Tile } from '../../sim/grid';

/**
 * Frames in the generated `tiles` texture, left to right. Static tiles draw their content art;
 * mowable tiles draw from their live sim state (grass → stripes).
 */
export const TILE_FRAMES = [
  'path',
  'dirt',
  'grass',
  'grass2',
  'grass3',
  'mowedN',
  'mowedE',
  'mowedS',
  'mowedW',
  'tallGrass',
  'sticks',
  'flowerBed',
  'mud',
  'water',
  'rock',
  'birdBath',
  'tree',
  'shrub',
  'gnome',
  'house',
  // Appended (never reorder: art sheets follow this layout).
  'sand',
  'bench',
  'lampPost',
  'bin',
  'barn',
  'fence',
  'wheat',
  'hayBale',
  'crops',
  'hedge',
  'trough',
  // Renderer-only: a flower bed the mower drove over (the sim tile is unchanged).
  'flowerBedTrampled',
  // Tall grass a reel has been through once (sim RoughGrass).
  'tallGrassCut',
  // A gnome the mower smashed (sim Ground where the art says gnome).
  'gnomeSmashed',
  // A bird bath the mower smashed.
  'birdBathSmashed',
  // Garden hose: the catalog frame (straight across), then one frame per connection mask
  // (N=1, E=2, S=4, W=8) chosen by the renderer from neighbors, and the chewed overlay.
  'hose',
  'hose0',
  'hose1',
  'hose2',
  'hose3',
  'hose4',
  'hose5',
  'hose6',
  'hose7',
  'hose8',
  'hose9',
  'hose10',
  'hose11',
  'hose12',
  'hose13',
  'hose14',
  'hose15',
  'hoseChewed',
  // A dog house, door facing down onto the lawn.
  'dogHouse',
  // A sprinkler head, and what is left when the mower breaks it.
  'sprinkler',
  'sprinklerBroken',
  // Lawn art: the judges' table (drive onto it to finish).
  'judgesTable',
  // Herding: the straw floor of the sheep pen (its fence is drawn round it), and the gate.
  'pen',
  'penGate',
  // Rival: the robot mower's charging dock.
  'roboDock',
  // The Golf Course: the putting green (and driven over), its flag pin, a ball washer.
  'green',
  'greenTrampled',
  'flag',
  'ballWasher',
  // Moles: a molehill pushed up through mowed lawn (sim Molehill).
  'molehill',
  // A tree the pruning saw cut down: a stump in the dirt (sim Ground).
  'treeStump',
  // A sprinkler the key turned off: the head closed flush with the lawn (sim Ground).
  'sprinklerOff',
  // A big tree's trunk and roots (its crown is a separate sprite: the crowns sheet).
  'bigTree',
  // Old Cemetery Grounds: a headstone, an old lantern, a thorn patch (flat tires), a dead tree,
  // and the log it leaves when it falls (across, along, and its branchy top end).
  'headstone',
  'lantern',
  'thorns',
  'deadTree',
  'log',
  'logV',
  'logTip',
  // Grand Estate: a cobblestone walk, a bridge (deck across: crossed east-west; and along), a
  // planted urn (and knocked over), a statue, a fountain, a topiary ball, the maze's hedge.
  'cobble',
  'bridge',
  'bridgeV',
  'urn',
  'urnSmashed',
  'statue',
  'fountain',
  'topiary',
  'mazeHedge',
  // Stadium: a goal (net and posts), a corner flag, a training cone (and knocked flying).
  'goal',
  'cornerFlag',
  'cone',
  'coneSmashed',
  // The catalog frame for a pitch line (on the lawn it is grass, its chalk drawn over it).
  'fieldLine',
  // Calm ground under a standing prop (the props sheet draws the thing itself).
  'lawnBase',
  // A fallen tree's top for its other three ways down ('logTip' is the fall to the east).
  'logTipW',
  'logTipN',
  'logTipS',
  // Break tiers: what a strong enough mower leaves of a bench, a bush, a potted topiary, a trash
  // bin, a golf flag pin, a corner flag and a ball washer.
  'benchSmashed',
  'shrubFlattened',
  'topiarySmashed',
  'binTipped',
  'flagDown',
  'cornerFlagDown',
  'ballWasherDown',
  // Rolling Meadows: wildflower meadow grass (it grows back if a patch is left half mowed).
  'meadowGrass',
  // A beehive (drawn as a prop standing on the lawn) and what is left of one smashed.
  'beehive',
  'beehiveSmashed',
  // Lawn Art: the show lawn round the picture (a neat diamond cut, nothing to mow).
  'showLawn',
  // Lawn Art's two picture tones, strongly apart: light (mowed up and down), dark (side to side).
  'artLight',
  'artDark',
] as const;
export type TileFrameName = (typeof TILE_FRAMES)[number];

export const TILE_FRAME_COUNT = TILE_FRAMES.length;

/**
 * What a mower leaves of a broken lawn item (break tiers), by its art. Only where a mower smashed
 * it (the view tracks that): a tool trimming a shrub or topiary away leaves plain ground.
 */
export const SMASHED_FRAME = {
  bench: 'benchSmashed',
  shrub: 'shrubFlattened',
  topiary: 'topiarySmashed',
  bin: 'binTipped',
  flag: 'flagDown',
  cornerFlag: 'cornerFlagDown',
  ballWasher: 'ballWasherDown',
  beehive: 'beehiveSmashed',
} as const satisfies Partial<Record<TileId, TileFrameName>>;

export const frameIndex = (name: TileFrameName): number => TILE_FRAMES.indexOf(name);

const MOWED: readonly TileFrameName[] = ['mowedN', 'mowedE', 'mowedS', 'mowedW'];

const GRASS: readonly TileFrameName[] = ['grass', 'grass2', 'grass3'];

/** Stable per-tile pick among variants, so big lawns don't look tiled. */
const variant = (x: number, y: number, n: number) => (((x * 73856093) ^ (y * 19349663)) >>> 0) % n;

/**
 * Which frame draws a tile, from its live sim state, last stripe direction, content art and
 * position (for visual variants).
 */
export function tileFrame(tile: Tile, stripe: Direction, art: TileId, x = 0, y = 0): number {
  switch (tile) {
    case Tile.Mowed:
      return frameIndex(MOWED[stripe] as TileFrameName);
    case Tile.Grass:
      if (art === 'meadowGrass') return frameIndex('meadowGrass');
      return frameIndex(GRASS[variant(x, y, GRASS.length)] as TileFrameName);
    case Tile.TallGrass:
      return frameIndex(art === 'wheat' ? 'wheat' : 'tallGrass');
    case Tile.RoughGrass:
      return frameIndex('tallGrassCut');
    case Tile.Molehill:
      return frameIndex('molehill');
    case Tile.Ground:
      // Ground that started as something else (sticks, a trimmed hedge) has been cleared to dirt.
      // A smashed decoration leaves its pieces.
      if (art === 'gnome') return frameIndex('gnomeSmashed');
      if (art === 'birdBath') return frameIndex('birdBathSmashed');
      if (art === 'sprinkler') return frameIndex('sprinklerBroken');
      if (art === 'judgesTable') return frameIndex('judgesTable');
      if (art === 'penGate') return frameIndex('penGate');
      if (art === 'tree' || art === 'deadTree') return frameIndex('treeStump');
      if (art === 'urn') return frameIndex('urnSmashed');
      if (art === 'cone') return frameIndex('coneSmashed');
      if (art === 'cobble' || art === 'bridge') return frameIndex(art);
      if (art === 'roboDock') return frameIndex('roboDock');
      if (art === 'showLawn') return frameIndex('showLawn');
      return frameIndex(art === 'path' || art === 'sand' ? art : 'dirt');
    default: {
      const i = frameIndex(art);
      return i >= 0 ? i : frameIndex('rock');
    }
  }
}
